import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
  TextInput,
} from 'react-native';
import PropTypes from 'prop-types';
import Clipboard from '@react-native-clipboard/clipboard';
import ModalFull from '@components/modal/ModalFull';
import Input from '@components/form/Input';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import { isAndroid } from '@global/screens';

const imageSize = 50;

const mapFieldName = (field) => {
  switch (field) {
    case 'sell_in':
      return 'Sell In';
    case 'sell_out':
      return 'Sell Out';
    case 'return':
      return 'Return';
    case 'quantity':
      return 'Quantity';
    case 'result_quantity':
      return 'Result Quantity';
    default:
      return field;
  }
};

const VisibilityValidationModal = ({
  visible = false,
  products = [],
  onUpdateField = () => {},
  onClose = () => {},
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeEditor, setActiveEditor] = useState(null);
  const [editorValues, setEditorValues] = useState({});

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (!visible) {
      setSearchQuery('');
      setDebouncedQuery('');
      setActiveEditor(null);
      setEditorValues({});
    }
  }, [visible]);

  const filteredMissingProducts = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return products;
    }
    const q = debouncedQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.product_name?.toLowerCase().includes(q) ||
        p.reference?.toLowerCase().includes(q),
    );
  }, [products, debouncedQuery]);

  const handleCopyReference = useCallback((ref) => {
    if (!ref) {
      return;
    }
    Clipboard.setString(ref);
  }, []);

  const getFieldEditorKey = useCallback((item, field) => {
    const productId = item?.product_id || '';
    const combinationId = item?.combination_id || '';
    return `${productId}-${combinationId}-${field}`;
  }, []);

  const getDefaultFieldValue = (item, field) => {
    const value = item?.current_values?.[field];
    if (value === undefined || value === null || value === '') {
      return '';
    }
    return String(value);
  };

  const commitFieldValue = useCallback(
    (item, field) => {
      const editorKey = getFieldEditorKey(item, field);
      const rawValue = editorValues?.[editorKey];
      onUpdateField({
        productId: item?.product_id,
        combinationId: item?.combination_id,
        reference: item?.reference,
        field,
        value: rawValue === undefined ? '' : rawValue,
      });
      setActiveEditor(null);
    },
    [editorValues, getFieldEditorKey, onUpdateField],
  );

  const handleNumericTextChange = useCallback((editorKey, val) => {
    if (/^\d*$/.test(val)) {
      setEditorValues((prev) => ({
        ...prev,
        [editorKey]: val,
      }));
    }
  }, []);

  const renderFieldChip = (item, field) => {
    const editorKey = getFieldEditorKey(item, field);
    const isActive = activeEditor?.key === editorKey;
    return (
      <TouchableOpacity
        key={editorKey}
        style={[
          styles.missingFieldChipButton,
          isActive && styles.missingFieldChipButtonActive,
        ]}
        activeOpacity={0.7}
        onPress={() => {
          setActiveEditor({
            key: editorKey,
            productId: item?.product_id,
            combinationId: item?.combination_id,
            field,
            reference: item?.reference,
          });
          setEditorValues((prev) => ({
            ...prev,
            [editorKey]:
              prev?.[editorKey] !== undefined
                ? prev[editorKey]
                : getDefaultFieldValue(item, field),
          }));
        }}>
        <SociollaText
          style={[
            styles.missingFieldItem,
            isActive && styles.missingFieldItemActive,
          ]}>
          {mapFieldName(field)}
        </SociollaText>
      </TouchableOpacity>
    );
  };

  const renderProduct = useCallback(
    ({ item }) => (
      <View style={styles.missingProductContainer}>
        <View style={styles.missingProductImageContainer}>
          <Image
            style={styles.image}
            width={imageSize}
            height={imageSize}
            source={item?.image || require('@assets/img/icons/no-product.svg')}
          />
        </View>
        <View style={styles.missingProductContent}>
          <View style={styles.missingProductHeader}>
            <SociollaText
              style={styles.missingProductName}
              type="medium"
              numberOfLines={2}>
              {item.product_name}
            </SociollaText>
            <View style={styles.missingCountBadge}>
              <SociollaText style={styles.missingCountBadgeText}>
                {item?.missing_fields?.length || 0} missing
              </SociollaText>
            </View>
          </View>
          {item.reference ? (
            <View style={styles.missingProductReferenceWrapper}>
              <SociollaText style={styles.missingProductReference}>
                {item.reference}
              </SociollaText>
              <TouchableOpacity
                style={styles.missingProductReferenceCopyButton}
                activeOpacity={0.6}
                onPress={() => handleCopyReference(item.reference)}>
                <Image
                  style={styles.missingProductReferenceCopyIcon}
                  width={18}
                  height={18}
                  source={require('@assets/img/icons/copy.svg')}
                  svg
                />
              </TouchableOpacity>
            </View>
          ) : null}
          <SociollaText style={styles.missingFieldsLabel}>
            Missing Fields:
          </SociollaText>
          <View style={styles.missingFieldsChipRow}>
            {item.missing_fields.map((field) => renderFieldChip(item, field))}
          </View>
          {activeEditor?.productId === item?.product_id &&
          (activeEditor?.combinationId || '') ===
            (item?.combination_id || '') ? (
            <View style={styles.missingFieldEditorInline}>
              <SociollaText style={styles.missingFieldEditorLabel}>
                {mapFieldName(activeEditor.field)}
              </SociollaText>
              <TextInput
                value={editorValues?.[activeEditor.key] ?? ''}
                onChangeText={(val) =>
                  handleNumericTextChange(activeEditor.key, val)
                }
                keyboardType="numeric"
                style={styles.missingFieldEditorInput}
                autoFocus
                returnKeyType="done"
                onSubmitEditing={() =>
                  commitFieldValue(item, activeEditor.field)
                }
                onBlur={() => commitFieldValue(item, activeEditor.field)}
              />
              <TouchableOpacity
                activeOpacity={0.7}
                style={styles.missingFieldEditorClose}
                onPress={() => setActiveEditor(null)}>
                <Image
                  style={styles.missingFieldEditorCloseIcon}
                  width={14}
                  height={14}
                  source={require('@assets/img/icons/close-grey.svg')}
                  svg
                />
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </View>
    ),
    [
      activeEditor,
      editorValues,
      getFieldEditorKey,
      handleCopyReference,
      commitFieldValue,
      handleNumericTextChange,
    ],
  );

  return (
    <ModalFull
      testID="todo-visibility-validation-modal"
      show={visible}
      title="Validation Fields"
      width="100%"
      onClose={onClose}>
      <View style={styles.missingFieldsContainer}>
        <View style={styles.missingSearchWrapper}>
          <Input
            placeholder="Search missing product"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
        <FlatList
          showsVerticalScrollIndicator={false}
          data={filteredMissingProducts}
          renderItem={renderProduct}
          keyExtractor={(item, index) => String(index)}
          ListEmptyComponent={
            <View style={styles.productEmpty}>
              <SociollaText style={styles.productEmptyText}>
                No missing fields found
              </SociollaText>
            </View>
          }
        />
      </View>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  productEmpty: {
    width: '100%',
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  productEmptyText: {
    fontSize: 13,
  },
  missingFieldsContainer: {
    paddingHorizontal: 12,
    paddingTop: 12,
    backgroundColor: Colors.white,
    height: '100%',
    paddingBottom: isAndroid ? 70 : 40,
  },
  missingSearchWrapper: {
    backgroundColor: Colors.grey16,
    marginBottom: 10,
  },
  missingProductContainer: {
    marginBottom: 10,
    padding: 10,
    borderRadius: 6,
    borderColor: Colors.grey0,
    borderWidth: 1,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  missingProductImageContainer: {
    width: 64,
    height: 64,
    borderRadius: 10,
    backgroundColor: Colors.grey16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  missingProductContent: {
    flex: 1,
  },
  missingProductHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  missingProductName: {
    flex: 1,
    fontSize: 15,
    lineHeight: 20,
    color: Colors.grey2,
    marginRight: 8,
  },
  missingCountBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  missingCountBadgeText: {
    fontSize: 11,
    color: Colors.grey1,
  },
  missingProductReferenceWrapper: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
  },
  missingProductReference: {
    fontSize: 13,
    color: Colors.grey1,
  },
  missingProductReferenceCopyButton: {
    marginLeft: 4,
    padding: 6,
  },
  missingProductReferenceCopyIcon: {
    width: 16,
    height: 16,
  },
  missingFieldsLabel: {
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 8,
    color: Colors.grey2,
  },
  missingFieldsChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  missingFieldChipButton: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    backgroundColor: Colors.grey16,
    borderWidth: 1,
    borderColor: Colors.grey0,
    marginRight: 6,
    marginBottom: 6,
  },
  missingFieldChipButtonActive: {
    borderColor: Colors.blue0,
    backgroundColor: Colors.white,
  },
  missingFieldItem: {
    fontSize: 12,
    color: Colors.grey1,
  },
  missingFieldItemActive: {
    color: Colors.blue0,
  },
  missingFieldEditorInline: {
    minWidth: 170,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.blue0,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    marginTop: 2,
    marginBottom: 6,
  },
  missingFieldEditorLabel: {
    fontSize: 11,
    color: Colors.grey1,
    marginRight: 6,
  },
  missingFieldEditorInput: {
    flex: 1,
    fontSize: 13,
    color: Colors.grey2,
    paddingVertical: 0,
    textAlign: 'left',
  },
  missingFieldEditorClose: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.grey16,
  },
  missingFieldEditorCloseIcon: {
    width: 12,
    height: 12,
  },
  image: { width: imageSize, height: imageSize },
});

export default React.memo(VisibilityValidationModal);

VisibilityValidationModal.propTypes = {
  visible: PropTypes.bool,
  products: PropTypes.array,
  onUpdateField: PropTypes.func,
  onClose: PropTypes.func,
};
